import React, { useCallback, useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    Box,
    TableContainer,
    TableFooter,
    TableHead,
    makeStyles,
    Theme,
    createStyles,
    Typography,
} from '@material-ui/core';
import i18n from 'i18n-js';
import {
    DishFinancial,
    CategoryFinancial,
    FinanceServiceDetails,
    RestaurantFinance,
} from 'api/models';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { Text } from 'components/shared';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        collapseRow: {
            '&:hover': {
                backgroundColor: 'unset !important',
            },
        },
        textAccent: {
            fontWeight: 600,
            fontSize: 13,
            color: theme.palette.primary.dark,
        },
        footerText: {
            fontWeight: 700,
            fontSize: 13,
            color: theme.palette.primary.dark,
        },
        textUpper: {
            textTransform: 'uppercase',
        },
        tableContainer: {
            maxHeight: 'calc(100vh - 210px)',
            minHeight: 500,
            overflow: 'auto',
        },
    })
);

function formatServiceValue(currency: string, service: FinanceServiceDetails) {
    if (!service?.totalItems)
        return (
            <>
                <div style={{ display: 'inline-block' }}>0 /</div>
                <div style={{ display: 'inline-block' }}>&nbsp;--</div>
            </>
        );

    return (
        <>
            <div style={{ display: 'inline-block' }}>{service.totalItems} /</div>
            <div style={{ display: 'inline-block' }}>
                &nbsp;{currency} {service.totalAmount.toFixed(2)}
            </div>
        </>
    );
}

function formatDisServiceValue(currency: string, service: FinanceServiceDetails) {
    if (!service?.totalItems) return <span>--</span>;

    return (
        <>
            <div style={{ display: 'inline-block' }}>{service.totalItems} /</div>
            <div style={{ display: 'inline-block' }}>
                &nbsp;{currency} {service.totalAmount.toFixed(2)}
            </div>
        </>
    );
}

interface DishTableProps {
    currency: string;
    dishes: DishFinancial[];
}

function DishTable({ currency, dishes }: DishTableProps) {
    const classes = useStyles();

    return (
        <>
            <Table size="small">
                <TableBody>
                    {dishes?.map((dish, index) => (
                        <TableRow key={index}>
                            <TableCell width={24}>
                                <div style={{ width: 20 }}></div>
                            </TableCell>
                            <TableCell width={'40%'}>
                                <Text text={dish.name} maxLength={50} withoutMore />
                            </TableCell>
                            <TableCell width={'10%'} align="right">
                                {formatDisServiceValue(currency, dish.homeDelivery)}
                            </TableCell>
                            <TableCell width={'10%'} align="right">
                                {formatDisServiceValue(currency, dish.takeAway)}
                            </TableCell>
                            <TableCell width={'10%'} align="right">
                                {formatDisServiceValue(currency, dish.dineIn)}
                            </TableCell>
                            <TableCell width={'10%'} align="right">
                                {dish.totalItems}
                            </TableCell>
                            <TableCell width={'20%'} align="right" className={classes.textAccent}>
                                {currency} {dish.totalAmount.toFixed(2)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}

interface FinanceSalesRowProps {
    row: CategoryFinancial;
    currency: string;
}

function FinanceSalesRow({ row, currency }: FinanceSalesRowProps) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const isDisabled = row.dishes.length === 0;

    const handleToggle = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    useEffect(() => {
        if (isDisabled) {
            setOpen(false);
        }
    }, [isDisabled]);

    return (
        <>
            <TableRow>
                <TableCell width={24}>
                    <IconButton size="small" disabled={isDisabled} onClick={handleToggle}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell width={'40%'}>
                    <Text text={row.name} maxLength={50} withoutMore />
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, row.homeDelivery)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, row.takeAway)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, row.dineIn)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {row.totalItems}
                </TableCell>
                <TableCell width={'20%'} align="right" className={classes.textAccent}>
                    {currency} {row.totalAmount.toFixed(2)}
                </TableCell>
            </TableRow>
            <TableRow className={classes.collapseRow}>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box marginLeft={1} marginRight={1}>
                            <DishTable dishes={row.dishes} currency={currency} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

interface FinanceSalesTableProps {
    currency: string;
    sales: RestaurantFinance;
}

export function FinanceSalesTable({ currency, sales }: FinanceSalesTableProps) {
    const classes = useStyles();

    return (
        <TableContainer className={classes.tableContainer}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell>{i18n.t('common.category')}</TableCell>
                        <TableCell>
                            <Box
                                display={'flex'}
                                alignItems={'end'}
                                justifyContent={'center'}
                                flexDirection={'column'}
                            >
                                <div>{i18n.t('form.homeDelivery')}</div>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    className={classes.textUpper}
                                >
                                    {i18n.t('common.items')} / {currency}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>
                            <Box
                                display={'flex'}
                                alignItems={'end'}
                                justifyContent={'center'}
                                flexDirection={'column'}
                            >
                                <div>{i18n.t('form.takeAway')}</div>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    className={classes.textUpper}
                                >
                                    {i18n.t('common.items')} / {currency}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>
                            <Box
                                display={'flex'}
                                alignItems={'end'}
                                justifyContent={'center'}
                                flexDirection={'column'}
                            >
                                <div>{i18n.t('form.dineIn')}</div>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    className={classes.textUpper}
                                >
                                    {i18n.t('common.items')} / {currency}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell align="right">{i18n.t('common.totalItems')}</TableCell>
                        <TableCell align="right" className={classes.textAccent}>
                            {i18n.t('common.totalAmount')}
                        </TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {sales?.categories?.map((cat, index) => (
                        <FinanceSalesRow key={index} row={cat} currency={currency} />
                    ))}
                </TableBody>

                <TableFooter>
                    <TableRow>
                        <TableCell
                            width={'40%'}
                            colSpan={2}
                            className={`${classes.footerText} ${classes.textUpper}`}
                        >
                            {i18n.t('common.total')}
                        </TableCell>
                        <TableCell width={'10%'} align="right" className={classes.footerText}>
                            {formatServiceValue(currency, sales.categoriesTotals.homeDelivery)}
                        </TableCell>
                        <TableCell width={'10%'} align="right" className={classes.footerText}>
                            {formatServiceValue(currency, sales.categoriesTotals.takeAway)}
                        </TableCell>
                        <TableCell width={'10%'} align="right" className={classes.footerText}>
                            {formatServiceValue(currency, sales.categoriesTotals.dineIn)}
                        </TableCell>
                        <TableCell width={'10%'} align="right" className={classes.footerText}>
                            {sales.categoriesTotals.totalItems}
                        </TableCell>
                        <TableCell width={'20%'} align="right" className={classes.footerText}>
                            {currency} {sales.categoriesTotals.totalAmount}
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
}
